import { Brand } from '@growtherapy/tailwind/react/types';

import { logger } from '../datadog/logger';
import { isDocumentVisible } from '../utils';

export type ShowSystemNotificationOptions = {
  title: string;
  body: string;
  brand?: Brand;
  /**
   * Handler run when the notification is actually displayed (the native
   * `show` event) — i.e. when it's genuinely "viewed".
   */
  onShow?: () => void;
  /**
   * Custom handler run when the user clicks the notification, after the tab is
   * refocused and before the notification is closed.
   */
  onClick?: () => void;
};

/**
 * Whether the browser supports the Web Notifications API. This is `false` on
 * iOS Safari (outside of installed PWAs), so the feature cleanly no-ops there.
 */
export function isSystemNotificationSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.Notification === 'function';
}

export function getNotificationPermission(): NotificationPermission | null {
  if (!isSystemNotificationSupported()) return null;
  return Notification.permission;
}

export function isNotificationPermissionGranted() {
  return getNotificationPermission() === 'granted';
}

export function isNotificationPermissionUndecided() {
  return getNotificationPermission() === 'default';
}

/**
 * Requests permission to show system notifications.
 */
export async function requestNotificationPermission(): Promise<
  NotificationPermission | 'user-gesture-required' | null
> {
  if (!isNotificationPermissionUndecided()) return null;

  try {
    const requestPermissionResult = await Notification.requestPermission();

    // Still undecided after the request means that a user gesture is required
    if (isNotificationPermissionUndecided()) {
      return 'user-gesture-required';
    }

    return requestPermissionResult;
  } catch (error) {
    logger.error('Error requesting system notification permission', {}, error as Error);
    return null;
  }
}

/**
 * Shows a system notification to grab a backgrounded user's attention.
 *
 * No-ops unless notifications are supported, permission has been granted, and
 * the tab is currently hidden.
 *
 * Clicking the notification refocuses the tab and closes it, and the
 * notification is auto-closed when the user returns to the tab.
 */
export function showSystemNotificationToBackgroundUser({
  title,
  body,
  brand,
  onShow,
  onClick,
}: ShowSystemNotificationOptions): Notification | void {
  if (!isNotificationPermissionGranted() || isDocumentVisible()) return;

  const icon = brand && (brand === 'arc' ? '/assets/favicon-arc.png' : '/assets/favicon.png');

  try {
    // shows the system notification
    const notification = new Notification(title, { body, icon });

    if (onShow) {
      notification.onshow = onShow;
    }

    notification.onclick = () => {
      window.focus();
      onClick?.();
      notification.close();
    };

    const closeWhenWindowVisible = () => {
      if (document.visibilityState === 'visible') {
        notification.close();
      }
    };
    notification.onclose = () => {
      document.removeEventListener('visibilitychange', closeWhenWindowVisible);
    };

    document.addEventListener('visibilitychange', closeWhenWindowVisible);

    return notification;
  } catch (error) {
    logger.warn('Unable to show system notification', {}, error as Error);
  }
}
